/**
 * Come un widget legge i suoi dati dalla memoria condivisa della dashboard.
 *
 *   const fatture = useFonte('fatture');
 *   if (fatture.stato === 'pronto') ... fatture.dati ...
 *
 * La prima volta che un widget chiede una fonte, questa si carica; gli altri
 * widget che chiedono la stessa fonte aspettano la stessa lettura.
 */

import { useCallback, useContext, useEffect, useSyncExternalStore } from 'react';
import { ContestoDati, type ValoreDatiDashboard } from '../components/dashboard/contestoDati';
import type { StatoFonte } from '../lib/dashboard/cacheFonti';
import type { ChiaveDati, DatiFonti } from '../lib/dashboard/fonti';

function useContestoDati(): ValoreDatiDashboard {
  const valore = useContext(ContestoDati);
  if (!valore) throw new Error('useFonte va usato dentro <DatiDashboard>');
  return valore;
}

export function useFonte<K extends ChiaveDati>(chiave: K): StatoFonte<DatiFonti[K]> {
  const { cache } = useContestoDati();
  const iscriviti = useCallback((avvisa: () => void) => cache.iscriviti(chiave, avvisa), [cache, chiave]);
  const istantanea = useCallback(() => cache.istantanea(chiave), [cache, chiave]);
  const stato = useSyncExternalStore(iscriviti, istantanea);

  useEffect(() => {
    cache.richiedi(chiave);
  }, [cache, chiave]);

  return stato;
}

/** Rilegge una fonte (il "Riprova" di un widget). */
export function useRicaricaFonte(): (chiave: ChiaveDati) => void {
  const { cache } = useContestoDati();
  return useCallback((chiave: ChiaveDati) => cache.ricarica(chiave), [cache]);
}

/** Il tasto "Aggiorna" della dashboard: rilegge tutto quello che si vede. */
export function useAggiornaDashboard(): () => void {
  const { cache } = useContestoDati();
  return useCallback(() => cache.ricaricaTutto(), [cache]);
}

/** La data di oggi in Italia, che la dashboard tiene aggiornata. */
export function useOggi(): string {
  return useContestoDati().oggi;
}
